import { ReloadOutlined } from '@ant-design/icons';
import { Checkbox, Modal, Select, Table } from 'antd';
import { ColumnProps } from 'antd/es/table';
import { observer } from 'mobx-react';
import React from 'react';
import { Button } from '../../components/button';
import { CreateButton, ExportButton } from '../../components/action-buttons';
import { PageTopBar } from '../../components/page-top-bar';
import { PaginationBar } from '../../components/pagination/pagination-bar';
import { SearchInput } from '../../components/search-input';
import { TableHeaderWithSubtitle } from '../../components/table-header-with-subtitle';
import {
    useApplicationSelectionStore,
    useAuthStore,
    useCategoryStore,
    useOfferStore,
    usePromotionStore,
} from '../../contexts/mobx';
import type { Promotion, PromotionSearchParams } from '../../stores/promotion';
import { ApplicationSelection } from '../../types/app-selection';
import type { ActiveQueryParam, PromotionTypes } from '../../utils/const';
import { getPromotionsTypeTitle } from '../../utils/promotion-types';
import { useNavigation } from '../../utils/use-navigation';
import { useSearchParams } from '../../utils/use-search-params';
import { useLocation } from 'react-router-dom';
import renderActivationCount from './components/render_activation_count';
import renderActivationDates from './components/render_activation_dates';
import renderActiveStatus from './components/render_active_status';
import renderDates from './components/render_dates';
import renderItem from './components/render_item';
import renderOffer from './components/render_offer';
import renderRegion from './components/render_region';
import renderTemporalStatus from './components/render_temporal_status';
import renderUsagePercentage from './components/render_usage_percentage';
import style from './style.module.scss';

export const CouponsCommonPage = observer(
    (props: { promotionType: PromotionTypes }) => {
        const { promotionType } = props;
        const promotionStore = usePromotionStore();
        const categoryStore = useCategoryStore();
        const offerStore = useOfferStore();
        const navigation = useNavigation();
        const authStore = useAuthStore();
        const applicationSelectionStore = useApplicationSelectionStore(); // Used for restricting the coupons to the selected application
        const isCoucheTard =
            applicationSelectionStore.selectedApplication ===
            ApplicationSelection.COUCHE_TARD;
        const { hasManagementAccess } = authStore;

        if (!hasManagementAccess) {
            navigation.goToHome();
        }

        const location = useLocation();
        const search = useSearchParams<PromotionSearchParams>();
        const hasInitializedFilterRef = React.useRef(false);
        const [
            shouldDefaultToActive,
            setShouldDefaultToActive,
        ] = React.useState(() => {
            const urlParams = new URLSearchParams(location.search);
            return !urlParams.has('filter');
        });

        const effectiveFilter = React.useMemo(() => {
            if (search.params.filter !== undefined) {
                return search.params.filter;
            }

            const urlParams = new URLSearchParams(location.search);
            if (urlParams.has('filter')) {
                return urlParams.get('filter') as ActiveQueryParam;
            }

            if (shouldDefaultToActive && !hasInitializedFilterRef.current) {
                return 'active' as ActiveQueryParam;
            }

            return undefined;
        }, [search.params.filter, location.search, shouldDefaultToActive]);

        React.useEffect(() => {
            if (!hasInitializedFilterRef.current) {
                const urlParams = new URLSearchParams(location.search);
                if (!urlParams.has('filter')) {
                    search.setParam('filter', 'active');
                }
                hasInitializedFilterRef.current = true;
                setShouldDefaultToActive(false);
            }
        }, [location.search, search]);

        const [searchValue, setSearchValue] = React.useState<string>(
            search.params.q || ''
        );
        const [reissueModalVisible, setReissueModalVisible] = React.useState(
            false
        );
        const [bypassHourCheck, setBypassHourCheck] = React.useState(false);

        React.useEffect(() => {
            const params = {
                ...search.params,
                filter: effectiveFilter,
                type: promotionType,
            };
            promotionStore.fetchPromotions(params);
        }, [
            search.params,
            effectiveFilter,
            promotionStore,
            promotionType,
            applicationSelectionStore.selectedApplication,
        ]);

        React.useEffect(() => {
            categoryStore.fetchActiveCategories();
        }, [categoryStore]);

        React.useEffect(() => {
            offerStore.fetchOffers();
        }, [offerStore]);

        const handleExportPromotions = async () => {
            await promotionStore.exportPromotions();
        };

        const handleReissueModalOpen = () => {
            setBypassHourCheck(false);
            setReissueModalVisible(true);
        };

        const handleReissueModalClose = () => {
            setReissueModalVisible(false);
            setBypassHourCheck(false);
        };

        const handleReissueTrigger = async () => {
            const success = await promotionStore.triggerReissue(
                bypassHourCheck
            );
            if (success) {
                handleReissueModalClose();
            }
        };

        const handleSearchClick = () =>
            search.setParams({ q: searchValue, page: 0 });
        const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value;
            setSearchValue(value);
            if (value === '') {
                search.setParams({ q: value, page: 0 });
            }
        };
        const handleOnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) =>
            e.key === 'Enter' && search.setParams({ q: searchValue, page: 0 });

        const clearFilters = () => {
            search.setParams({
                q: undefined,
                type: undefined,
                filter: undefined,
                categoryId: undefined,
                promotionalOfferId: undefined,
                isPrivate: undefined,
                page: 0,
            });
            setSearchValue('');
        };

        const columns: ColumnProps<Promotion>[] = [
            {
                key: 'active_status',
                title: 'Active',
                render: renderActiveStatus,
                width: 100,
                className: 'active-status-column',
            },
            {
                key: 'temporal_status',
                title: 'Period Status',
                render: renderTemporalStatus,
                width: 140,
                className: 'temporal-status-column',
            },
            {
                key: 'image',
                title: 'Image',
                render: (_, record) => renderItem(record, 'thumbnail'),
                width: 100,
                className: 'image-column',
            },
            {
                title: 'Title',
                key: 'title',
                render: (_, record) => renderItem(record, 'title'),
                className: 'title-column',
                width: 250,
            },
            {
                title: 'Subtitle',
                key: 'subtitle',
                render: (_, record) => renderItem(record, 'subTitle'),
                className: 'subtitle-column',
                width: 200,
            },
            {
                title: 'Activations',
                key: 'activation_count',
                render: renderActivationCount,
                width: 120,
                className: 'activations-column',
            },
            {
                title: 'Usage %',
                key: 'usage_percentage',
                render: renderUsagePercentage,
                width: 100,
                className: 'usage-column',
            },
            {
                title: 'Region',
                key: 'provinces',
                render: renderRegion,
                width: 100,
                className: 'region-column',
            },
            {
                title: (
                    <TableHeaderWithSubtitle
                        title="App Visibility"
                        subtitle="Start date / End date"
                    />
                ),
                key: 'dates',
                render: renderDates,
                width: 220,
                className: 'dates-column',
            },
            {
                title: (
                    <TableHeaderWithSubtitle
                        title="Activation Period"
                        subtitle="Start date / End date"
                    />
                ),
                key: 'activation_period',
                render: renderActivationDates,
                width: 220,
                className: 'activation-period-column',
            },
            {
                title: 'Tags',
                key: 'tags',
                render: renderOffer,
                className: 'tags-column',
                width: 200,
            },
        ];

        return (
            <>
                <PageTopBar title={getPromotionsTypeTitle(promotionType)}>
                    <>
                        <ExportButton
                            onClick={handleExportPromotions}
                            loading={promotionStore.isExportingPromotions}
                        >
                            Export to CSV
                        </ExportButton>
                        {hasManagementAccess && (
                            <Button
                                variant="secondary"
                                icon={<ReloadOutlined />}
                                onClick={handleReissueModalOpen}
                                loading={promotionStore.isTriggeringReissue}
                            >
                                Trigger Reissue
                            </Button>
                        )}
                        <CreateButton
                            onClick={
                                promotionType === 'coupon'
                                    ? navigation.goToCouponCreate
                                    : navigation.goToInStoreCouponCreate
                            }
                        />
                    </>
                </PageTopBar>

                <div className="action_zone_above_table">
                    <div className="table_header_options_bar">
                        <div className={style.selects_row}>
                            <div style={{ width: 220 }}>
                                <SearchInput
                                    searchValue={searchValue}
                                    onClickIcon={handleSearchClick}
                                    onChange={handleOnChange}
                                    onKeyDown={handleOnKeyDown}
                                />
                            </div>
                            <Select<ActiveQueryParam>
                                placeholder="Status"
                                value={effectiveFilter}
                                onChange={(v: ActiveQueryParam | undefined) => {
                                    search.setParam('filter', v);
                                }}
                                allowClear
                            >
                                <Select.Option value="active">
                                    Active
                                </Select.Option>
                                <Select.Option value="inactive">
                                    Inactive
                                </Select.Option>
                            </Select>
                            <Select<number>
                                placeholder="Category"
                                defaultValue={undefined}
                                onChange={(id: number | undefined) =>
                                    search.setParam('categoryId', id)
                                }
                                value={search.params.categoryId}
                                allowClear
                                loading={
                                    categoryStore.isFetchingActiveCategories
                                }
                            >
                                {categoryStore.activeCategories?.results
                                    .filter(
                                        (category) =>
                                            !isCoucheTard ||
                                            !category.requireAgeVerification
                                    )
                                    .map((category) => (
                                        <Select.Option
                                            value={category.id}
                                            key={category.id}
                                        >
                                            {category.items[0].title}
                                        </Select.Option>
                                    ))}
                            </Select>
                            <Select<number>
                                placeholder="Promotional Offer"
                                defaultValue={undefined}
                                onChange={(id: number | undefined) =>
                                    search.setParam('promotionalOfferId', id)
                                }
                                value={search.params.promotionalOfferId}
                                allowClear
                                loading={offerStore.isFetchingOffers}
                            >
                                {offerStore.offers?.results.map((offers) => (
                                    <Select.Option
                                        value={offers.id}
                                        key={offers.id}
                                    >
                                        {offers.items[0].title}
                                    </Select.Option>
                                ))}
                            </Select>
                            <Select<string>
                                placeholder="Winnable"
                                value={search.params.isPrivate}
                                onChange={(e) =>
                                    search.setParam('isPrivate', e)
                                }
                                allowClear
                            >
                                <Select.Option value="yes">
                                    Winnable promotions
                                </Select.Option>
                                <Select.Option value="no">
                                    Normal promotions
                                </Select.Option>
                            </Select>
                            <Button
                                variant="secondary-destructive"
                                onClick={clearFilters}
                            >
                                Clear Filters
                            </Button>
                        </div>
                    </div>

                    <PaginationBar
                        pagination={{
                            page: search.params.page,
                            pageSize: search.params.perPage,
                            total: promotionStore.promotions?.total,
                            onPageChange: (page) => search.setParams({ page }),
                            onPageSizeChange: (perPage) =>
                                search.setParams({ perPage, page: 0 }),
                        }}
                    />
                </div>

                <div className={style.table_scroll_container}>
                    <Table<Promotion>
                        className={`${style.table} clickable_table_body_rows`}
                        scroll={undefined}
                        rowKey="id"
                        dataSource={promotionStore.promotions?.results}
                        columns={columns}
                        loading={promotionStore.isFetchingCount > 0}
                        pagination={false}
                        onRow={(r) => ({
                            onClick: () =>
                                props.promotionType === 'coupon'
                                    ? navigation.goToCouponEdit(r.id)
                                    : navigation.goToInStoreCouponEdit(r.id),
                        })}
                        size="small"
                        bordered
                    />
                </div>

                <Modal
                    title="Trigger Promotion Reissue"
                    open={reissueModalVisible}
                    onOk={handleReissueTrigger}
                    onCancel={handleReissueModalClose}
                    confirmLoading={promotionStore.isTriggeringReissue}
                    okText="Trigger Reissue"
                    cancelText="Cancel"
                >
                    <div className={style.reissue_modal_content}>
                        <p className={style.reissue_modal_description}>
                            This will trigger the promotion reissue process for
                            eligible activations.
                        </p>
                        <Checkbox
                            checked={bypassHourCheck}
                            onChange={(e) =>
                                setBypassHourCheck(e.target.checked)
                            }
                            className={style.reissue_modal_checkbox}
                        >
                            <strong>Bypass 24-hour check</strong>
                        </Checkbox>
                        <div className={style.reissue_modal_info}>
                            {bypassHourCheck ? (
                                <>
                                    <strong>Bypass mode:</strong> Reissue ALL
                                    active promotion activations with
                                    auto-reissue enabled, regardless of when
                                    they were activated.
                                </>
                            ) : (
                                <>
                                    <strong>Normal mode:</strong> Only reissue
                                    promotions that were activated 23-25 hours
                                    ago (standard 24-hour window).
                                </>
                            )}
                        </div>
                    </div>
                </Modal>
            </>
        );
    }
);
